import {
  LAYER_ID_TENANT_SEPARATOR,
  resolveTopologicalPath,
  STANDARD_BRANCH,
} from '@festina-life/api'
import { withLifeErrorBoundary } from '@festina-life/components'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { IconButton } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import { Tree, type TreeNodeData } from '@festina-life/ui/listsAndTables'
import { Icon, Stack, typographyStyles } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useRef } from 'react'

import { useDraftContext } from '../../context/draftContext'
import { useMetaModelContext } from '../../context/metaModelContext'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useIsPersonaEditor } from '../../uiModels/useIsPersonaEditor'
import { useWorkspaceOperations } from '../../uiModels/workspace/useWorkspaceOperations'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { ConfigurationLeaf } from './ConfigurationLeaf'
import {
  extractLayerAndPackageFromPath,
  extractLocationFromTreeNode,
  layerKeyFromPath,
} from './helpers'
import { StickyActiveConfigurationItemPath } from './StickyActiveConfigurationItemPath'
import { ConfigurationLeafTags, type ConfigurationTreeLeaf } from './types'
import { useConfigurationItemsTreeId } from './useConfigurationItemsTreeId'
import { useHoveredConfigurationItem } from './useHoveredConfigurationItem'
import { useNodePathToActiveConfigurationItem } from './useNodePathToActiveConfigurationItem'
import { useScrollPathTracking } from './useScrollPathTracking'

type Props = {
  tree: TreeNodeData<ConfigurationTreeLeaf>[]
  query?: Optional<string>
  isVisible: boolean
}

/**
 * Shows the configuration items that are available for a given server. Allows the user to select an item
 * from the list that they can proceed to configure it.
 */
const ServerConfigurationItemsInternal = ({
  tree,
  query,
  isVisible,
}: Props) => {
  const translate = useTranslate()

  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const { branch } = useNavigationParams()
  const treeContainerRef = useRef<HTMLDivElement>(null)
  const stickyPathRef = useRef<HTMLDivElement>(null)
  const treeId = useConfigurationItemsTreeId()
  const { layerTopology } = useMetaModelContext()
  const { allLayers: workspaceLayers } =
    useWorkspaceOperations(currentWorkspaceKey)
  const { onConfigureNewDraft } = useDraftContext()
  const isPersonaEditor = useIsPersonaEditor()
  const shouldShowAddButton =
    !!branch && branch !== STANDARD_BRANCH && isPersonaEditor
  const { currentNodePath, isStickyPathVisible } = useScrollPathTracking({
    treeContainerRef,
    stickyPathRef,
    tree,
    query,
    isVisible,
  })
  const currentScrollConfigurationItem = useNodePathToActiveConfigurationItem(
    tree,
    currentNodePath,
  )
  const {
    hoveredConfigurationItem,
    startHoveredConfigurationItem,
    clearHoveredConfigurationItem,
  } = useHoveredConfigurationItem()

  const displayedConfigurationItem = isStickyPathVisible
    ? (hoveredConfigurationItem ?? currentScrollConfigurationItem)
    : undefined

  return (
    <div
      className="h-full overflow-y-auto overflow-x-hidden pb-12 pl-2"
      ref={treeContainerRef}
    >
      {tree.length ? (
        <>
          <StickyActiveConfigurationItemPath
            ref={stickyPathRef}
            activeConfigurationItem={displayedConfigurationItem}
          />
          <Tree
            id={treeId}
            isVisible={isVisible}
            query={query}
            containerRef={treeContainerRef.current}
            nodes={tree}
            nodeClassName={(node) => {
              const isDraft = node.tags.includes(ConfigurationLeafTags.draft)
              const nodeLayerKey = layerKeyFromPath(node.path)
              const isWorkspaceUsingLayer =
                nodeLayerKey &&
                workspaceLayers.some((workspaceLayer) =>
                  workspaceLayer.equals(nodeLayerKey),
                )
              const isLayerOpaque =
                currentWorkspaceKey && !isWorkspaceUsingLayer

              return cx(
                'py-1',
                {
                  'text-draft-600 dark:text-draft-400 font-medium': isDraft,
                },
                typographyStyles({
                  intensity: isLayerOpaque ? 'tertiary' : 'primary',
                }),
              )
            }}
            renderNode={(node) => {
              const isLayer =
                node.path.at(-1)?.includes(LAYER_ID_TENANT_SEPARATOR) ?? false
              const { layerKeys, packageParts } =
                extractLayerAndPackageFromPath(node.path)

              return (
                <Stack
                  className="flex gap-x-1"
                  gap="condensed"
                  data-tree-scroll-path={node.path.join('.')}
                  extraProps={{
                    onMouseEnter: () =>
                      startHoveredConfigurationItem({
                        item: node,
                        layers: layerKeys,
                        packageName: packageParts.join('.'),
                      }),
                    onMouseLeave: clearHoveredConfigurationItem,
                  }}
                >
                  <Icon
                    name={isLayer ? 'Square3Stack3D' : 'Folder'}
                    color="unset"
                  />
                  {node.name}
                </Stack>
              )
            }}
            renderNodeActions={
              shouldShowAddButton
                ? (node) => (
                    <IconButton
                      iconName="Plus"
                      iconSize="sm"
                      tip={translate('serverConfigurationItems.addDraft')}
                      className="opacity-0 group-hover:opacity-100 p-0!"
                      onClick={(e) => {
                        e.stopPropagation()
                        const { layer: locationLayer, packagePath } =
                          extractLocationFromTreeNode(node.path)

                        assertNotEmpty(locationLayer)
                        void onConfigureNewDraft(
                          { type: undefined, value: { packagePath } },
                          locationLayer,
                        )
                      }}
                    />
                  )
                : undefined
            }
            leafClassName="py-1"
            renderLeaf={(leaf) => {
              const isCurrentLayerEditedInCurrentWorkspace =
                workspaceLayers.some((workspaceLayer) =>
                  workspaceLayer.equals(leaf.layer),
                )

              const leafLayers = resolveTopologicalPath(
                leaf.layer,
                layerTopology,
              )
              const leafConfigurationItem = {
                item: leaf,
                layers: leafLayers,
                packageName: leaf.packageName,
              }

              return (
                <ConfigurationLeaf
                  key={leaf.id}
                  leaf={leaf}
                  isCurrentLayerEdited={isCurrentLayerEditedInCurrentWorkspace}
                  configurationItemPath={configurationItem}
                  onHoverStart={() =>
                    startHoveredConfigurationItem(leafConfigurationItem)
                  }
                  onHoverEnd={clearHoveredConfigurationItem}
                />
              )
            }}
          />
        </>
      ) : (
        <div className="p-2">
          <Alert severity="error" className="col-span-2 h-min">
            {translate('configurationItems.empty')}
          </Alert>
        </div>
      )}
    </div>
  )
}

export const ServerConfigurationItems = withSuspense(
  withLifeErrorBoundary(ServerConfigurationItemsInternal),
)
